/**
 * Platform billing metrics (superadmin) — MRR-schatting, churn, CSV-export
 */
import { useQuery, useMutation } from '@tanstack/react-query';
import { BarChart3, Download } from 'lucide-react';
import { Breadcrumbs } from '../../components/Breadcrumbs';
import { SeoMeta } from '../../components/SeoMeta';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/AuthContext';
import { Navigate } from 'react-router-dom';

export function AdminBillingPlatform() {
  const { user } = useAuth();
  const isSuper = user?.role === 'superadmin';

  const { data, isLoading, error } = useQuery({
    queryKey: ['billing-platform-metrics'],
    queryFn: () => api.getBillingPlatformMetrics(),
    enabled: !!user?.authenticated && isSuper,
  });

  const exportMut = useMutation({
    mutationFn: async () => {
      const blob = await api.exportBillingPlatformCsv();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'billing_subscriptions.csv';
      a.click();
      URL.revokeObjectURL(url);
    },
  });

  if (!isSuper) {
    return <Navigate to="/admin" replace />;
  }

  const errMsg = error instanceof Error ? error.message : String(error ?? '');

  return (
    <div>
      <SeoMeta
        path="/admin/billing-platform"
        noindex
        title="Platform facturatie"
        description="Platform-MRR, churn en Stripe-metrics voor CalqTool™ — export en inzichten, alleen superadmin."
      />
      <Breadcrumbs items={[{ label: 'Platformbeheer', href: '/admin' }, { label: 'Platform billing' }]} />
      <div className="mb-6 flex items-center gap-2">
        <BarChart3 className="h-6 w-6 text-primary" aria-hidden />
        <h1 className="text-2xl font-bold text-text">Platform billing</h1>
      </div>
      <p className="mb-6 max-w-2xl text-sm text-text-muted">
        Samenvatting over alle tenants (D1). MRR is een <strong>schatting</strong> op basis van actieve abonnementen
        (jaar → /12). Voor productie-export kun je ook{' '}
        <code className="rounded bg-surface px-1 text-xs">X-Billing-Ops-Secret</code> gebruiken (zie docs).
      </p>

      {isLoading && <p className="text-text-muted">Laden…</p>}
      {error && (
        <p className="mb-4 rounded-lg bg-amber-50 p-3 text-sm text-amber-900 dark:bg-amber-950/40 dark:text-amber-100">
          {errMsg}
        </p>
      )}

      {data && (
        <div className="space-y-6">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <div className="rounded-xl border border-border bg-surface-card p-4">
              <p className="text-xs text-text-muted">MRR (schatting)</p>
              <p className="text-2xl font-bold text-text">€ {data.mrr_eur}</p>
            </div>
            <div className="rounded-xl border border-border bg-surface-card p-4">
              <p className="text-xs text-text-muted">Nieuwe subs (30d)</p>
              <p className="text-2xl font-bold text-text">{data.new_subscriptions_30d}</p>
            </div>
            <div className="rounded-xl border border-border bg-surface-card p-4">
              <p className="text-xs text-text-muted">Churn (30d, status canceled)</p>
              <p className="text-2xl font-bold text-text">{data.churned_subscriptions_30d}</p>
            </div>
            <div className="rounded-xl border border-border bg-surface-card p-4">
              <p className="text-xs text-text-muted">Open facturen</p>
              <p className="text-2xl font-bold text-text">{data.open_invoices}</p>
            </div>
            <div className="rounded-xl border border-border bg-surface-card p-4">
              <p className="text-xs text-text-muted">Tenants past_due</p>
              <p className="text-2xl font-bold text-text">{data.past_due_tenants}</p>
            </div>
          </div>

          <div className="rounded-xl border border-border bg-surface-card p-4">
            <h2 className="mb-2 text-sm font-semibold text-text">Actief per plan</h2>
            <ul className="text-sm text-text-muted">
              {(data.active_subscriptions_rows ?? []).map((r, i) => (
                <li key={`${r.plan_id}-${r.interval}-${i}`}>
                  {r.plan_id} / {r.interval}: <strong className="text-text">{r.c}</strong>
                </li>
              ))}
              {(data.active_subscriptions_rows ?? []).length === 0 && <li>Geen actieve abonnementen.</li>}
            </ul>
          </div>

          <button
            type="button"
            disabled={exportMut.isPending}
            onClick={() => exportMut.mutate()}
            className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
          >
            <Download className="h-4 w-4" />
            {exportMut.isPending ? 'Exporteren…' : 'Export subscriptions CSV'}
          </button>
          {exportMut.isError && (
            <p className="text-sm text-red-600">{exportMut.error instanceof Error ? exportMut.error.message : 'Export mislukt'}</p>
          )}

          <p className="text-xs text-text-muted">Gegenereerd: {new Date(data.generated_at).toLocaleString('nl-NL')}</p>
        </div>
      )}
    </div>
  );
}
